import { setStatus, setTheme } from '../scenario.slice';
import {
    IJson,
    IRootState,
    TInitWidgetState,
    TTriggerWidgetAction,
    ValidationErrors,
    IJsonScenario,
    EStatus,
    ActionTypeEnum,
    IPayloadScenario,
} from '@libs/types';
import { isLoadingAction } from '@libs/utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchJson } from '@libs/network';
import { initWidgetState, CFAccessDeniedError } from '@libs/network-utils';
import { resolveScenarioTheme } from '../../service/ThemeService';

const getPayload = (scenario: IJsonScenario): IPayloadScenario => {
    const { mutableWidgetsMap, bottomSheetMap, modalWindowMap, internalIdWidgets, systemActions, submitWidget, rootWidgets } = initWidgetState(
        scenario.pages,
    ) as TInitWidgetState;
    return {
        scenarioId: scenario?.scenarioId,
        currentPageId: scenario?.pages?.[0]?.id,
        configuration: scenario?.configuration ?? {},
        bottomSheetMap,
        mutableWidgetsMap,
        submitWidget,
        internalIdWidgets,
        modalWindowMap,
        systemActions,
        rootWidgets,
    };
};

const applyScenarioTheme = async (scenario: IJsonScenario, dispatch: (action: unknown) => void) => {
    const theme = await resolveScenarioTheme(scenario);
    if (theme) dispatch(setTheme(theme));
};

export const fetchScenario = createAsyncThunk<
    IPayloadScenario | undefined,
    TTriggerWidgetAction,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('fetchScenario', async ({ action, widgetUid, isShowLoader, keepalive, playerId, initPayerOptions, scenarioId, currentPageId }, thunkAPI) => {
    try {
        const { scenario } = thunkAPI.getState();
        let json = await fetchJson(
            action,
            isShowLoader || isLoadingAction(action),
            widgetUid,
            scenario.currentPageId,
            scenario.mutableWidgetsMap,
            (status: EStatus) => {
                thunkAPI.dispatch(setStatus(status));
            },
            keepalive,
            playerId,
            initPayerOptions,
        );

        const isDelayedProductAction =
            (action?.type === ActionTypeEnum.Product || action?.type === ActionTypeEnum.ProductJump) && action?.delay && action.delay > 0;

        if (!isDelayedProductAction) {
            await applyScenarioTheme(json.scenario, thunkAPI.dispatch);
            return getPayload(json.scenario);
        }

        const isSamePage = json.scenario.scenarioId === scenarioId && json.scenario.pages[0]?.id === currentPageId;

        if (isSamePage) {
            json = await fetchJson(
                undefined,
                true,
                widgetUid,
                scenario.currentPageId,
                scenario.mutableWidgetsMap,
                (status: EStatus) => {
                    thunkAPI.dispatch(setStatus(status));
                },
                keepalive,
                playerId,
                initPayerOptions,
            );
        }
        await applyScenarioTheme(json.scenario, thunkAPI.dispatch);
        return getPayload(json.scenario);
    } catch (e: unknown) {
        const isCFAccessDeniedError = e instanceof CFAccessDeniedError;
        console.error(`fetchScenario: ${widgetUid ?? ''}; ${action?.name ?? ''}  ${e}`);
        if (e instanceof Error && e.name === 'AbortError') {
            console.log('fetchScenario: request was aborted by core');
        } else if (isCFAccessDeniedError) {
            console.log('fetchScenario: CFAccessDeniedError');
            return thunkAPI.rejectWithValue({
                errorMessage: 'CF started',
                isForbidden: true,
                captchaBody: (e as CFAccessDeniedError).getBody(),
                widgetUid: widgetUid,
                actionForRepeat: action,
            });
        } else {
            return thunkAPI.rejectWithValue({
                errorMessage: 'Response do not have any scenario!',
            });
        }
    }
});

export const fetchJsonAsync = createAsyncThunk<
    IJson,
    TTriggerWidgetAction,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('fetchJsonAsync', async ({ action, isShowLoader, keepalive, widgetUid, playerId }, thunkAPI) => {
    try {
        const { scenario } = thunkAPI.getState();
        return await fetchJson(
            action,
            isShowLoader ?? isLoadingAction(action),
            widgetUid,
            scenario.currentPageId,
            scenario.mutableWidgetsMap,
            (status: EStatus) => {
                thunkAPI.dispatch(setStatus(status));
            },
            keepalive,
            playerId,
        );
    } catch (e: unknown) {
        const isCFAccessDeniedError = e instanceof CFAccessDeniedError;
        console.error(`fetchJsonAsync: ${widgetUid ?? ''}; ${action?.name ?? ''} ${e}`);
        if (e instanceof Error && e.name === 'AbortError') {
            console.log('fetchJsonAsync: request was aborted by core');
            throw e;
        } else if (isCFAccessDeniedError) {
            console.log('fetchJsonAsync: CFAccessDeniedError');
            return thunkAPI.rejectWithValue({
                errorMessage: 'CF started',
                isForbidden: true,
                captchaBody: (e as CFAccessDeniedError).getBody(),
                widgetUid: widgetUid,
                actionForRepeat: action,
            });
        } else {
            return thunkAPI.rejectWithValue({
                errorMessage: 'Response do not have any scenario!',
            });
        }
    }
});

export const updateScenario = createAsyncThunk<
    IPayloadScenario | undefined,
    IJson,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('updateScenario', async (json, thunkAPI) => {
    await applyScenarioTheme(json.scenario, thunkAPI.dispatch);
    return getPayload(json.scenario);
});
